import React, { useContext, useEffect, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { useLocation } from "wouter";
import {
    mdiAntenna, mdiCogOutline, mdiFileCogOutline, mdiFileDocumentOutline,
    mdiFileQuestionOutline, mdiTextBoxOutline, mdiDatabaseOutline,
    mdiAccountMultipleOutline, mdiHeartMultipleOutline,
    mdiFileDocumentMultipleOutline
} from "@mdi/js";

import resourceRest from "../../rest/resourceRest";

import { Svg } from "../components/Svg";
import { Line } from "../components/Line";
import { Spacer } from "../components/Spacer";
import { UserContext } from "../contexts/UserContext";

const NavigationStyle = styled.div`
    width: 280px;
    min-width: 190px;
    padding-top: 26px;
    vertical-align: top;
    background-color: ${props => props.theme.white};
    border-right: 1px solid ${props => props.theme.greyLight};
`;

const TitleStyle = styled.p`
    font-size: 14px;
    margin-left: 30px;
    margin-bottom: 20px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${props => props.theme.black};
`;

const PointStyle = styled.div`
    display: flex;
    margin: 7px 0;
    cursor: pointer;
    padding: 8px 30px;
    color: ${props => props.theme.black};
    :nth-child(1) {
        flex: 0 0 auto;
    }
    :nth-child(2) {
        padding: 2px 12px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &:hover {
        color: ${props => props.theme.black};
        background-color: ${props => props.theme.greyLight};
    }
    &.selected {
        color: ${props => props.theme.white};
        background-color: ${props => props.theme.primaryLight};
        &:hover {
            color: ${props => props.theme.white};
            background-color: ${props => props.theme.primaryLighter};
        }
    }
`;

const Point = ({ value, route, svgIcon, changed = false }) => {
    const [location, setLocation] = useLocation();
    const parts = location.split("/");
    const currentRoute = parts.pop() || parts.pop(); //handle potential trailing slash
    return (
        <PointStyle
            className={(route === currentRoute ? "selected" : "")}
            onClick={() => setLocation("/" + route)}>
            <Svg
                highlight={changed}
                path={svgIcon}
                size="18" />
            <div>{value}</div>
        </PointStyle>
    );
};

export const Navigation = () => {

    const userContext = useContext(UserContext);
    const [documentsChanged, setDocumentsChanged] = useState({
        news: false,
        manual: false,
        agreement: false,
    });

    useEffect(() => {
        const fetchDocumentChecksums = async () => {
            const localChecksums = JSON.parse(localStorage.getItem("documentChecksums") || "{}");
            const serverChecksums = await resourceRest.getDocumentChecksums();

            setDocumentsChanged(prev => {
                const updated = { ...prev };
                Object.entries(serverChecksums).forEach(([key, serverValue]) => {
                    const hasChanged = localChecksums[key] === undefined || localChecksums[key] !== serverValue;
                    if (prev[key] === false && hasChanged === true) {
                        updated[key] = true;
                    }
                });
                return updated;
            });
        };

        fetchDocumentChecksums();
        const intervalId = setInterval(fetchDocumentChecksums, 5000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const docName = location.pathname.replace("/", "");
        if (!["news", "manual", "agreement"].includes(docName)) {
            return;
        }
        const markAsRead = async () => {
            const server = await resourceRest.getDocumentChecksums();
            const local = JSON.parse(localStorage.getItem("documentChecksums") || "{}");
            local[docName] = server[docName];
            localStorage.setItem("documentChecksums", JSON.stringify(local));
            setDocumentsChanged(prev => ({ ...prev, [docName]: false }));
        };
        markAsRead();
    }, [location]);

    return (
        <NavigationStyle>
            <TitleStyle>{intl.get("navigation.navigation")}</TitleStyle>
            <Point
                route="entries"
                svgIcon={mdiTextBoxOutline}
                value={intl.get("navigation.entries")} />
            <Point
                route="antennas"
                svgIcon={mdiAntenna}
                value={intl.get("navigation.antennas")} />
            <Line />
            <Point
                route="news"
                changed={documentsChanged.news}
                svgIcon={mdiFileQuestionOutline}
                value={intl.get("navigation.news")} />
            <Point
                route="manual"
                changed={documentsChanged.manual}
                svgIcon={mdiFileCogOutline}
                value={intl.get("navigation.manual")} />
            <Point
                route="agreement"
                changed={documentsChanged.agreement}
                svgIcon={mdiFileDocumentOutline}
                value={intl.get("navigation.agreement")} />
            <Line />
            <Point
                route="settings"
                svgIcon={mdiCogOutline}
                value={intl.get("navigation.settings")} />
            {userContext?.user?.administrator &&
                <>
                    <Spacer variant="horizontal" size="large" />
                    <Spacer variant="horizontal" size="large" />
                    <TitleStyle>{intl.get("navigation.administration")}</TitleStyle>
                    <Point
                        route="runners"
                        svgIcon={mdiHeartMultipleOutline}
                        value={intl.get("navigation.runners")} />
                    <Point
                        route="users"
                        svgIcon={mdiAccountMultipleOutline}
                        value={intl.get("navigation.users")} />
                    <Point
                        route="documents"
                        svgIcon={mdiFileDocumentMultipleOutline}
                        value={intl.get("navigation.documents")} />
                    <Point
                        route="worstcaseData"
                        svgIcon={mdiDatabaseOutline}
                        value={intl.get("navigation.worstcaseData")} />
                </>
            }
        </NavigationStyle>
    );
};